import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRight, Play, TrendingUp, Users, Target } from 'lucide-react';
import { Card } from './ui/Card';
export function Hero() {
  return <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden" data-id="element-114">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-slate-950" data-id="element-115">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" data-id="element-116" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-117" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-118">
        <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-119">
          {/* Text Content */}
          <div className="flex-1 text-center lg:text-left" data-id="element-120">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-121">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6" data-id="element-122">
                <span className="relative flex h-2 w-2" data-id="element-123">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" data-id="element-124"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-125"></span>
                </span>
                Nexus AI Engine v2.0 Live
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white tracking-tight mb-6 leading-tight" data-id="element-126">
                Stop Guessing. <br data-id="element-127" />
                Start{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400" data-id="element-128">
                  Dominating.
                </span>
              </h1>

              <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-129">
                Butineurjeu leverages predictive quantum bidding to optimize
                your ad spend in real-time. Turn every impression into a
                conversion with the world's most advanced ad intelligence
                platform.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-130">
                <Button size="lg" rightIcon={<ArrowRight className="w-4 h-4" data-id="element-132" />} data-id="element-131">
                  Start Free Trial
                </Button>
                <Button variant="outline" size="lg" leftIcon={<Play className="w-4 h-4" data-id="element-134" />} data-id="element-133">
                  Watch Demo
                </Button>
              </div>

              <div className="mt-10 flex items-center justify-center lg:justify-start gap-8 text-slate-500 text-sm" data-id="element-135">
                <div className="flex items-center gap-2" data-id="element-136">
                  <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-137" />
                  <span data-id="element-138">+245% Avg. ROI</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-139">
                  <Users className="w-4 h-4 text-emerald-500" data-id="element-140" />
                  <span data-id="element-141">10k+ Marketers</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Hero Visual / Dashboard Mockup */}
          <div className="flex-1 w-full max-w-[600px] lg:max-w-none" data-id="element-142">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative" data-id="element-143">
              {/* Main Dashboard Card */}
              <Card className="relative z-10 bg-slate-900 border-slate-800 shadow-2xl shadow-emerald-900/20 p-6" data-id="element-144">
                {/* Header */}
                <div className="flex items-center justify-between mb-8" data-id="element-145">
                  <div data-id="element-146">
                    <h3 className="text-slate-200 font-semibold" data-id="element-147">
                      Campaign Performance
                    </h3>
                    <p className="text-xs text-slate-500" data-id="element-148">
                      Last 24 Hours • Real-time
                    </p>
                  </div>
                  <div className="flex gap-2" data-id="element-149">
                    <div className="h-2 w-2 rounded-full bg-red-500/20" data-id="element-150" />
                    <div className="h-2 w-2 rounded-full bg-yellow-500/20" data-id="element-151" />
                    <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-152" />
                  </div>
                </div>

                {/* Chart Area Placeholder */}
                <div className="h-48 w-full flex items-end justify-between gap-2 mb-8" data-id="element-153">
                  {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 100].map((h, i) => <motion.div key={i} initial={{
                  height: 0
                }} animate={{
                  height: `${h}%`
                }} transition={{
                  duration: 1,
                  delay: 0.5 + i * 0.05
                }} className="w-full bg-gradient-to-t from-emerald-500/10 to-emerald-500 rounded-t-sm relative group" data-id="element-154">
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-xs text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-slate-700" data-id="element-155">
                          {h * 124} Impressions
                        </div>
                      </motion.div>)}
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-4" data-id="element-156">
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50" data-id="element-157">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-158">Total Spend</p>
                    <p className="text-lg font-bold text-white" data-id="element-159">4,291 PLN</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50" data-id="element-160">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-161">Conversions</p>
                    <p className="text-lg font-bold text-emerald-400" data-id="element-162">842</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50" data-id="element-163">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-164">CPC</p>
                    <p className="text-lg font-bold text-white" data-id="element-165">1.24 PLN</p>
                  </div>
                </div>
              </Card>

              {/* Floating Elements */}
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -right-8 -top-8 z-20" data-id="element-166">
                <Card className="p-4 bg-slate-800 border-slate-700 shadow-xl w-48" data-id="element-167">
                  <div className="flex items-center gap-3 mb-2" data-id="element-168">
                    <div className="p-2 rounded-md bg-emerald-500/20 text-emerald-400" data-id="element-169">
                      <Target className="w-4 h-4" data-id="element-170" />
                    </div>
                    <div data-id="element-171">
                      <p className="text-xs text-slate-400" data-id="element-172">Optimization</p>
                      <p className="text-sm font-bold text-white" data-id="element-173">Target Hit</p>
                    </div>
                  </div>
                  <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden" data-id="element-174">
                    <div className="bg-emerald-500 h-full w-[92%]" data-id="element-175" />
                  </div>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}